/** The company's consent to accounting firms (M14): connect, limit roles, revoke. */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Briefcase } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { shortDate } from '../../lib/format';
import { Alert, Button, Card, EmptyState, Field, PageHeader, Skeleton, StatusChip, inputClass } from '../../components/ui';
import { ReasonDialog } from '../../components/ReasonDialog';
import { errorText } from '../integrations/shared';

interface Link { id: string; firm: string; code: string; status: 'PENDING' | 'ACTIVE' | 'DECLINED' | 'REVOKED'; allowedRoles: string[]; billing: string; connectedAt: string; staff: { name: string; email: string; role: string; active: boolean }[] }
const ROLES = ['Accountant', 'Bookkeeper', 'Tax Officer', 'Auditor'];

function RolePicker({ value, onChange, disabled }: { value: string[]; onChange: (v: string[]) => void; disabled?: boolean }): JSX.Element {
  return (
    <div className="flex flex-wrap gap-3">
      {ROLES.map((r) => (
        <label key={r} className="flex items-center gap-2 text-sm">
          <input type="checkbox" disabled={disabled} checked={value.includes(r)} onChange={(e) => onChange(e.target.checked ? [...value, r] : value.filter((x) => x !== r))} /> {r}
        </label>
      ))}
    </div>
  );
}

export function AdvisorFirmsPage(): JSX.Element {
  const { can } = useAuth();
  const qc = useQueryClient();
  const manage = can('users.manage');
  const list = useQuery({ queryKey: ['company-advisors'], queryFn: () => api<Link[]>('/company/advisors') });
  const [code, setCode] = useState('');
  const [roles, setRoles] = useState(['Accountant', 'Bookkeeper', 'Tax Officer']);
  const [billing, setBilling] = useState('DIRECT');
  const [revoking, setRevoking] = useState<Link | null>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'critical'; text: string } | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['company-advisors'] });
  const connect = useMutation({
    mutationFn: () => api<{ firm: string }>('/company/advisors', { method: 'POST', body: { code, allowedRoles: roles, billing } }),
    onSuccess: (r) => { refresh(); setCode(''); setMessage({ tone: 'ok', text: `${r.firm} is connected. Its partners can now assign staff to your books in the roles you allowed.` }); },
    onError: (e) => setMessage({ tone: 'critical', text: errorText(e) }),
  });
  const update = useMutation({ mutationFn: ({ id, body }: { id: string; body: unknown }) => api(`/company/advisors/${id}`, { method: 'PATCH', body }), onSuccess: refresh, onError: (e) => setMessage({ tone: 'critical', text: errorText(e) }) });
  const revoke = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api(`/company/advisors/${id}/revoke`, { method: 'POST', body: { reason } }),
    onSuccess: () => { refresh(); setRevoking(null); setMessage({ tone: 'ok', text: 'Access revoked. The firm’s staff lost access to this company immediately.' }); },
  });
  return (
    <>
      <PageHeader title="Advisor Firms" description="Accounting and bookkeeping firms you allow to work on your books. You choose the roles; you can end access at any time." />
      {message && <div className="mb-4"><Alert tone={message.tone}>{message.text}</Alert></div>}
      {manage && (
        <Card title="Connect a firm" description="Ask the firm for its code. Connecting is your consent for its staff to process your company's data." className="mb-4">
          <form className="grid gap-4 md:grid-cols-2" onSubmit={(e) => { e.preventDefault(); connect.mutate(); }}>
            <Field label="Firm code" required><input className={inputClass + ' uppercase'} value={code} onChange={(e) => setCode(e.target.value)} placeholder="F-XXXXXX" required /></Field>
            <Field label="Billing" hint="Who pays for this company's subscription.">
              <select className={inputClass} value={billing} onChange={(e) => setBilling(e.target.value)}><option value="DIRECT">Billed to us directly</option><option value="FIRM">Billed to the firm (wholesale)</option></select>
            </Field>
            <div className="md:col-span-2">
              <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">Roles the firm may assign</p>
              <RolePicker value={roles} onChange={setRoles} />
            </div>
            <div className="md:col-span-2 flex justify-end"><Button type="submit" busy={connect.isPending} disabled={!roles.length}><Briefcase aria-hidden="true" className="h-4 w-4" /> Connect firm</Button></div>
          </form>
        </Card>
      )}
      {list.isLoading && <Skeleton className="h-32 w-full" />}
      {list.data?.length === 0 && <Card><EmptyState title="No firms connected" description="Your accountant's firm can manage your books alongside your own team." /></Card>}
      <div className="space-y-4">
        {list.data?.map((l) => (
          <Card
            key={l.id}
            title={l.firm}
            description={`${l.code} · connected ${shortDate(l.connectedAt)} · billed ${l.billing === 'FIRM' ? 'to the firm' : 'directly'}`}
            actions={<div className="flex items-center gap-2"><StatusChip tone={l.status === 'ACTIVE' ? 'ok' : 'neutral'}>{l.status.toLowerCase()}</StatusChip>{manage && l.status === 'ACTIVE' && <Button size="sm" variant="danger" onClick={() => setRevoking(l)}>Revoke access</Button>}</div>}
          >
            <div className="space-y-3">
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Roles allowed</p>
                <RolePicker value={l.allowedRoles} disabled={!manage || l.status !== 'ACTIVE' || update.isPending} onChange={(v) => v.length && update.mutate({ id: l.id, body: { allowedRoles: v } })} />
              </div>
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Firm staff on your books</p>
                {l.staff.length === 0 ? <p className="text-sm text-slate-500">None assigned yet.</p> : (
                  <ul className="space-y-1 text-sm">
                    {l.staff.map((s) => <li key={s.email} className={s.active ? '' : 'opacity-50'}>{s.name} <span className="text-slate-500">{s.email}</span> · {s.role}{s.active ? '' : ' (ended)'}</li>)}
                  </ul>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
      {revoking && <ReasonDialog title={`Revoke ${revoking.firm}?`} description="Every seat the firm holds on this company ends immediately. Work already done stays in the books and the audit trail." confirmLabel="Revoke access" busy={revoke.isPending} onClose={() => setRevoking(null)} onConfirm={(reason) => revoke.mutate({ id: revoking.id, reason })} />}
    </>
  );
}
